import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";
import DynamicTable from "../../../components/lab-dynamic-table";
import DateRangeSelector from "../../../components/DateRangeSelector";

interface ReferringDoctor {
  _id: string;
  doctorName: string;
  referralCommission?: number;
}

interface DoctorShareRow {
  _id: string;
  trNo?: number | string;
  collectionDate?: string;
  createdAt?: string;
  patientName?: string;
  doctorName?: string;
  referralCommission?: number;
  doctorShare?: number;
  collectionCenterName?: string;
  grossAmount?: number;
  discount?: number;
  paidAmount?: number;
  dueAmount?: number;
  netAmount?: number;
  tests?: string;
}

const formatAmount = (value?: number) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const formatDisplayDate = (value?: string) => {
  if (!value) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DoctorShareSummary: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];
  const printedBy =
    profile?.data?.fullName || profile?.data?.firstName || "User";

  const [rows, setRows] = useState<DoctorShareRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("all");
  const [referringDoctors, setReferringDoctors] = useState<ReferringDoctor[]>(
    []
  );
  const [referringDrId, setReferringDrId] = useState<string>("");

  const fetchReferringDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/referring-doctor/getAllBySearch`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setReferringDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching referring doctors:", error);
      setReferringDoctors([]);
    }
  };

  const fetchDoctorShareSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getDoctorShareSummary`,
        {
          params: {
            count: rowsPerPage,
            pageNo: page + 1,
            fromDate,
            toDate,
            ...(selectedCollectionCenterId !== "all" && {
              collectionCenterId: selectedCollectionCenterId,
            }),
            ...(referringDrId && { referringDrId }),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setRows(response.data.data || []);
        setTotalRecords(
          response.data.totalCount || response.data.data?.length || 0
        );
      } else {
        toast.error(
          response.data?.message || "Failed to fetch doctor share summary"
        );
        setRows([]);
        setTotalRecords(0);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Error fetching doctor share summary"
      );
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferringDoctors();
  }, []);

  useEffect(() => {
    fetchDoctorShareSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromDate,
    toDate,
    page,
    rowsPerPage,
    selectedCollectionCenterId,
    referringDrId,
  ]);

  const handleCollectionCenterChange = (event: SelectChangeEvent) => {
    setSelectedCollectionCenterId(event.target.value);
    setPage(0);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totals = rows.reduce(
    (acc, row) => ({
      doctorShare: acc.doctorShare + Number(row.doctorShare || 0),
      grossAmount: acc.grossAmount + Number(row.grossAmount || 0),
      discount: acc.discount + Number(row.discount || 0),
      paidAmount: acc.paidAmount + Number(row.paidAmount || 0),
      dueAmount: acc.dueAmount + Number(row.dueAmount || 0),
    }),
    {
      doctorShare: 0,
      grossAmount: 0,
      discount: 0,
      paidAmount: 0,
      dueAmount: 0,
    }
  );

  const handleExcelExport = () => {
    if (!rows.length) {
      toast.warn("No data to export.");
      return;
    }

    const worksheetData = rows.map(row => ({
      "T/R No.": row.trNo ?? "-",
      "Collection centre": row.collectionCenterName || "-",
      "Collection date": formatDisplayDate(row.collectionDate || row.createdAt),
      Patient: row.patientName || "-",
      Doctor: row.doctorName || "-",
      "Doctor share %": row.referralCommission ?? 0,
      "Doctor share": Number(row.doctorShare || 0),
      "Gross charges": Number(row.grossAmount || 0),
      Discount: Number(row.discount || 0),
      "Paid amount": Number(row.paidAmount || 0),
      "Due amount": Number(row.dueAmount || 0),
      Tests: row.tests || "-",
    }));

    worksheetData.push({
      "T/R No.": "",
      "Collection centre": "",
      "Collection date": "",
      Patient: "TOTAL",
      Doctor: "",
      "Doctor share %": "" as any,
      "Doctor share": totals.doctorShare,
      "Gross charges": totals.grossAmount,
      Discount: totals.discount,
      "Paid amount": totals.paidAmount,
      "Due amount": totals.dueAmount,
      Tests: "",
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Doctor Share Summary");
    xlsx.writeFile(
      workbook,
      `Doctor_Share_Summary_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const handlePrint = () => {
    if (!rows.length) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Print date: ${new Date().toLocaleString()}`,
        margin,
        yPosition
      );
    doc.text(`Printed by: ${printedBy}`, pageWidth - margin, yPosition, {
      align: "right",
    });

    yPosition += 6;
    doc
      .setFontSize(11)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("Doctor share summary report", margin, yPosition);
    doc
      .setFontSize(9)
      .setFont("helvetica", "normal")
      .text(
        `( ${formatDisplayDate(fromDate)} To ${formatDisplayDate(toDate)} )`,
        pageWidth - margin,
        yPosition,
        { align: "right" }
      );

    yPosition += 4;
    doc
      .setDrawColor(180, 180, 180)
      .setLineWidth(0.3)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 3;

    const head = [
      [
        "T/R No.",
        "Collection centre",
        "Collection date",
        "Patient",
        "Doctor",
        "Doctor share",
        "Gross charges",
        "Discount",
        "Paid amount",
        "Due amount",
        "Tests",
      ],
    ];

    const body = rows.map(row => [
      row.trNo ?? "-",
      row.collectionCenterName || "-",
      formatDisplayDate(row.collectionDate || row.createdAt),
      row.patientName || "-",
      row.doctorName || "-",
      formatAmount(row.doctorShare),
      formatAmount(row.grossAmount),
      formatAmount(row.discount),
      formatAmount(row.paidAmount),
      formatAmount(row.dueAmount),
      row.tests || "-",
    ]);

    body.push([
      "",
      "",
      "",
      "TOTAL",
      "",
      formatAmount(totals.doctorShare),
      formatAmount(totals.grossAmount),
      formatAmount(totals.discount),
      formatAmount(totals.paidAmount),
      formatAmount(totals.dueAmount),
      "",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      styles: {
        fontSize: 6.5,
        cellPadding: 1.5,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 6.5,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
      },
      margin: { left: margin, right: margin },
      didParseCell: data => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [230, 230, 230];
        }
      },
    });

    doc.save(`Doctor_Share_Summary_${fromDate}_to_${toDate}.pdf`);
  };

  const headers = [
    "T/R No.",
    "Collection centre",
    "Collection date",
    "Patient",
    "Doctor",
    "Doctor share",
    "Gross charges",
    "Discount",
    "Paid amount",
    "Due amount",
    "Tests",
  ];

  const tableData: React.ReactNode[][] = rows.map(row => [
    row.trNo ?? "-",
    row.collectionCenterName || "-",
    formatDisplayDate(row.collectionDate || row.createdAt),
    row.patientName || "-",
    row.doctorName || "-",
    formatAmount(row.doctorShare),
    formatAmount(row.grossAmount),
    formatAmount(row.discount),
    formatAmount(row.paidAmount),
    formatAmount(row.dueAmount),
    row.tests || "-",
  ]);

  if (rows.length > 0) {
    tableData.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      formatAmount(totals.doctorShare),
      formatAmount(totals.grossAmount),
      formatAmount(totals.discount),
      formatAmount(totals.paidAmount),
      formatAmount(totals.dueAmount),
      "",
    ]);
  }

  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Collection Center
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "180px" } }}
            >
              <Select
                value={selectedCollectionCenterId}
                onChange={handleCollectionCenterChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ width: { xs: "100%", sm: 220 } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Referring Doctor
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={referringDoctors}
              getOptionLabel={option => option.doctorName || ""}
              value={
                referringDoctors.find(doc => doc._id === referringDrId) || null
              }
              onChange={(_event, newValue) => {
                setReferringDrId(newValue?._id || "");
                setPage(0);
              }}
              renderInput={params => (
                <TextField {...params} placeholder="All doctors" />
              )}
            />
          </Box>

          <DateRangeSelector
            compact
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={value => {
              setDateRange(value);
              setPage(0);
            }}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box sx={{ p: 1, maxHeight: "60vh", overflowY: "hidden" }}>
        <DynamicTable
          headers={headers}
          data={tableData}
          loading={loading}
          height={"60vh"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords > 0 ? totalRecords : rows.length}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default DoctorShareSummary;
